import { threadsApi } from '@/api/threads.api';
import { ActionButton } from '@/components/common/ActionButton';
import { ChoiceCellEditor, DROPDOWN_CONTROL, DROPDOWN_OVERLAY_PT } from '@/components/common/ChoiceCellEditor';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { MOTION } from '@/components/common/motion';
import { PageHeader } from '@/components/common/PageHeader';
import { Reveal } from '@/components/common/Reveal';
import { TableSkeleton } from '@/components/common/Skeleton';
import { SECTION_LABEL } from '@/components/common/surfaces';
import { ConfirmDeleteDialog } from '@/components/dialogs/ConfirmDeleteDialog';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import { ThreadTableDropZone } from '@/components/threads/ThreadTableDropZone';
import { useThreadSpecs } from '@/hooks/useThreadSpecs';
import type {
  CreateThreadSpec,
  ThreadMethod,
  ThreadSpecModel,
  ThreadTableImportReport,
  ThreadType,
  UpdateThreadSpec,
} from '@/types/thread.types';
import { downloadBlob } from '@/utils/download';
import {
  TABLE_CARD,
  TABLE_CELL,
  TABLE_CELL_NUM,
  TABLE_FOOTER,
  TABLE_HEADER_CELL,
  TABLE_HEADER_ROW,
  TABLE_ROW,
} from '@/utils/stickyColumns';
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  DownloadSimple,
  ListNumbers,
  Plus,
  Question,
  Trash,
  UploadSimple,
  WarningCircle,
} from '@phosphor-icons/react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import type { DropdownPassThroughOptions } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import type { FC, MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

/** Placeholder for a value the thread table leaves blank. */
const EMPTY_VALUE = '—';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** The table's columns, in display order, with the alignment each one reads best in. */
const THREAD_COLUMNS: { key: string; labelKey: string; align: 'left' | 'right' }[] = [
  { key: 'designation', labelKey: 'threads.colDesignation', align: 'left' },
  { key: 'nominalDiameter', labelKey: 'threads.colNominalDiameter', align: 'right' },
  { key: 'pitch', labelKey: 'threads.colPitch', align: 'right' },
  { key: 'preDrillDiameter', labelKey: 'threads.colPreDrillDiameter', align: 'right' },
  { key: 'threadType', labelKey: 'threads.colType', align: 'left' },
  { key: 'depth', labelKey: 'threads.colDepth', align: 'right' },
  { key: 'depthOffset', labelKey: 'threads.colDepthOffsetA', align: 'right' },
  { key: 'method', labelKey: 'threads.colMethod', align: 'left' },
];

/** Name the export is saved under, matching what the import expects back. */
const EXPORT_FILENAME = 'thread_table.csv';

/** Column id of the trailing actions column, which has no label of its own. */
const ACTIONS_COL_KEY = '_actions';

/** Pinned to the left edge while the rest of the table scrolls under it. */
const PINNED_COLUMN = 'designation';

/** Default column widths in px; the two dropdown columns get room for their longest label. */
const DEFAULT_COL_WIDTHS: Record<string, number> = {
  designation: 144,
  nominalDiameter: 128,
  pitch: 112,
  preDrillDiameter: 144,
  threadType: 144,
  depth: 128,
  depthOffset: 112,
  method: 176,
  [ACTIONS_COL_KEY]: 72,
};

/** Narrowest a column may be dragged, so a header never collapses out of reach. */
const MIN_COL_WIDTH = 48;

/**
 * Pinned columns, mirroring the assemblies table.
 *
 * `bg-inherit` keeps a pinned cell opaque without freezing its colour: the row owns the
 * background including the hover, and the cell simply takes whatever the row computes to.
 *
 * Below `md` the pinning is switched off, because two pinned columns on a 400 px screen ate
 * the viewport and the middle of the table could not be reached. It is the *offset* that is
 * dropped, not the `sticky`: `left-auto` leaves the cell positioned — which is what the
 * pending-edit accent anchors to — while giving it nothing to stick to.
 */
const PINNED_LEFT = 'sticky left-auto md:left-0 bg-inherit shadow-[inset_-1px_0_0_#e5e7eb]';
const PINNED_RIGHT = 'sticky right-auto md:right-0 bg-inherit shadow-[inset_1px_0_0_#e5e7eb]';

/** Pinned header cells ride above the pinned body cells, which ride above the scrolling ones. */
const PINNED_BODY_Z = 'z-10';
const PINNED_HEADER_Z = 'z-20';

/**
 * Paint containment on the card holding the horizontally scrolling table.
 *
 * A table wider than its `overflow-x-auto` parent is clipped visually, but Chrome still adds
 * its width to the scrollable overflow of every ancestor up to the document — so on a 400 px
 * screen the whole page gained a horizontal scrollbar. Paint containment stops that, because
 * it makes the card an explicit clipping boundary, and it is safe for the pinned columns:
 * `position: sticky` resolves against the inner scroll container, which is untouched.
 */
const CARD_CONTAINMENT = '[contain:paint]';

/** The page-size picker in the table footer: a quiet 32 px control, not a form field. */
const PAGE_SIZE_DROPDOWN_PT: DropdownPassThroughOptions = {
  root: { className: `${DROPDOWN_CONTROL} w-auto! min-w-0!` },
  input: { className: 'p-0! text-sm! tabular-nums text-gray-700! shadow-none!' },
  trigger: { className: 'ml-1! flex w-auto! shrink-0 items-center text-gray-400!' },
  ...DROPDOWN_OVERLAY_PT,
};

/** The one interactive value inside a cell: plain text that lights up on hover. */
const CELL_VALUE_CLASSES = `block w-full truncate rounded px-2 py-1 text-gray-700 hover:bg-gray-100 ${MOTION} active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`;

const LS_COL_WIDTHS_KEY = 'threadManagement.colWidths';

/**
 * Stored widths merged over the defaults, keeping only entries that still name a real column
 * and carry a usable number. A layout saved before the columns changed therefore degrades to
 * the defaults for the parts it no longer describes, rather than rendering a broken header.
 */
function loadColWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(LS_COL_WIDTHS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, unknown>;
      const usable: Record<string, number> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (key in DEFAULT_COL_WIDTHS && typeof value === 'number' && Number.isFinite(value) && value >= MIN_COL_WIDTH) {
          usable[key] = value;
        }
      }
      return { ...DEFAULT_COL_WIDTHS, ...usable };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_COL_WIDTHS };
}

/** The numeric columns a user may edit. All are millimetres except `pitch`. */
type NumericField = 'pitch' | 'pre_drill_diameter_mm' | 'alternative_depth_mm' | 'depth_offset_a_mm';

/** The two columns edited by picking from a list rather than by typing. */
type ChoiceField = 'thread_type' | 'method';

/** Every editable column; `designation` and `nominal_diameter_mm` identify the row and stay read-only. */
type EditableField = NumericField | ChoiceField;

/** Pending edits, keyed by thread-spec id so they survive paging and filtering. */
type ThreadEditsMap = Record<string, UpdateThreadSpec>;

/** The one nullable numeric column: clearing it sends an explicit `null`. */
const NULLABLE_NUMERIC: NumericField = 'depth_offset_a_mm';

/**
 * A millimetre value with at most two decimals and no trailing zeros: `3`, `1.25`, `0.5`.
 *
 * The table mixes whole-millimetre depths with pitches like `0.35`, so a fixed two-decimal
 * format would print a column of `3.00`s that is harder to scan than the chart it mirrors.
 */
function formatMm(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/** The add dialog holds raw text so a half-typed number never becomes `NaN` mid-edit. */
interface AddThreadForm {
  nominalDiameter: string;
  pitch: string;
  preDrillDiameter: string;
  depth: string;
  depthOffset: string;
  threadType: ThreadType;
  method: ThreadMethod | null;
  designation: string;
}

const EMPTY_ADD_FORM: AddThreadForm = {
  nominalDiameter: '',
  pitch: '',
  preDrillDiameter: '',
  depth: '',
  depthOffset: '',
  threadType: 'standard',
  method: null,
  designation: '',
};

/**
 * Read one of the form's numeric fields, or `null` when it is not a positive number.
 *
 * Czech keyboards produce a decimal comma, and the table's own designations use one, so a
 * comma is accepted wherever a point is.
 */
function parsePositive(raw: string): number | null {
  const text = raw.trim().replace(',', '.');
  if (text === '') return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * The designation the backend will derive: `M6` for a standard thread, `M6x0.5` for a fine
 * one. Mirrored here so the dialog can show what it is about to create.
 */
function deriveDesignation(diameter: number | null, pitch: number | null, threadType: ThreadType): string {
  if (diameter == null) return '';
  const base = `M${formatMm(diameter)}`;
  if (threadType !== 'fine' || pitch == null) return base;
  return `${base}x${formatMm(pitch)}`;
}

/** The add dialog's numeric fields, in the order the dialog reads them. */
const ADD_NUMERIC_FIELDS = ['nominalDiameter', 'pitch', 'preDrillDiameter', 'depth', 'depthOffset'] as const;

type AddNumericField = (typeof ADD_NUMERIC_FIELDS)[number];

/** The one optional numeric field: left blank it is fine, filled in it still has to parse. */
const ADD_OPTIONAL_FIELD: AddNumericField = 'depthOffset';

/**
 * Every field of the add dialog that can be wrong, in the order the dialog lays them out —
 * which is also the order a rejected submit walks looking for the first one to put the caret in.
 */
const ADD_FIELDS = [...ADD_NUMERIC_FIELDS, 'method'] as const;

type AddField = (typeof ADD_FIELDS)[number];

/**
 * What is wrong with one field of the add dialog, if anything.
 *
 * `invalid` and `missing` are kept apart because they differ in timing as well as in wording:
 * text that cannot be a number is wrong the moment it is typed, while a blank required field
 * is only worth complaining about once the user has left it or pressed the submit button.
 */
type AddFieldStatus = 'ok' | 'invalid' | 'missing';

function addFieldStatus(raw: string, optional: boolean): AddFieldStatus {
  if (raw.trim() === '') return optional ? 'ok' : 'missing';
  return parsePositive(raw) == null ? 'invalid' : 'ok';
}

/** What an error does to a control: a forced red border, and a soft red ring while at rest. */
const ADD_INVALID_CONTROL = 'border-red-500! ring-2 ring-red-500/20';

/** The label of one field, red from the moment the field has something to say. */
function addFieldLabelClasses(invalid: boolean): string {
  return `flex flex-col gap-1 text-sm ${MOTION} ${invalid ? 'text-red-600' : 'text-gray-700'}`;
}

/** The message row under a field, and the element its input points `aria-describedby` at. */
const AddFieldMessage: FC<{ id: string; message: string }> = ({ id, message }) => (
  <span id={id} className="mt-1 flex items-center gap-1 text-xs text-red-600">
    <WarningCircle size={14} className="shrink-0" aria-hidden="true" />
    {message}
  </span>
);

interface AddNumberFieldProps {
  field: AddNumericField;
  label: string;
  value: string;
  /** Message to show under the input, or `null` while the field has nothing to say. */
  error: string | null;
  onChange: (field: AddNumericField, value: string) => void;
  onBlur: (field: AddNumericField) => void;
  registerRef: (field: AddNumericField, el: HTMLInputElement | null) => void;
}

/**
 * One numeric field of the add dialog, carrying the error state the whole dialog shares.
 *
 * The error is told three ways at once — the label, the border and a message row — rather than
 * by the border alone: a single red outline in a grid of six identical boxes is neither easy to
 * spot nor available to a screen reader, which is what `aria-describedby` is doing here.
 *
 * The border utilities are forced because `main.tsx` loads the PrimeReact theme after the app's
 * own stylesheet, so an unforced border colour loses the resting state to the theme and the
 * focused state to the accent override in `styles/index.css`.
 */
const AddNumberField: FC<AddNumberFieldProps> = ({ field, label, value, error, onChange, onBlur, registerRef }) => {
  const messageId = useId();
  const setInputRef = useCallback((el: HTMLInputElement | null) => registerRef(field, el), [field, registerRef]);
  const invalid = error != null;

  return (
    <label className={addFieldLabelClasses(invalid)}>
      {label}
      <InputText
        ref={setInputRef}
        value={value}
        inputMode="decimal"
        onChange={(e) => onChange(field, e.target.value)}
        onBlur={() => onBlur(field)}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? messageId : undefined}
        className={`p-inputtext-sm ${MOTION} ${invalid ? `${ADD_INVALID_CONTROL} focus:border-red-500!` : ''}`}
      />
      {invalid ? <AddFieldMessage id={messageId} message={error} /> : null}
    </label>
  );
};

interface AddMethodFieldProps {
  label: string;
  value: ThreadMethod | null;
  options: { label: string; value: string }[];
  placeholder: string;
  error: string | null;
  onChange: (value: ThreadMethod | null) => void;
  /** The user has been at the field: a pick, a panel opened and closed, or a tab straight past. */
  onTouched: () => void;
  dropdownRef: RefObject<Dropdown | null>;
}

/**
 * The production method — the one required field of the dialog that is chosen, not typed.
 *
 * Two things differ from a numeric field, and both are forced by the widget. The red border
 * goes through the passthrough's `root`, merged rather than replacing it, because that is
 * where the rest of the control's look already comes from; and `.p-focus` stands in for
 * `:focus`, because what takes the caret is a hidden input inside the control rather than the
 * control itself — which is also why the aria attributes are handed to `Dropdown` as props,
 * from where PrimeReact forwards them to that input.
 */
const AddMethodField: FC<AddMethodFieldProps> = ({
  label,
  value,
  options,
  placeholder,
  error,
  onChange,
  onTouched,
  dropdownRef,
}) => {
  const messageId = useId();
  const invalid = error != null;

  return (
    <label className={`col-span-2 ${addFieldLabelClasses(invalid)}`}>
      {label}
      <Dropdown
        ref={dropdownRef}
        value={value}
        options={options}
        onChange={(e) => onChange((e.value ?? null) as ThreadMethod | null)}
        onHide={onTouched}
        onBlur={onTouched}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? messageId : undefined}
        pt={invalid ? { root: { className: `${ADD_INVALID_CONTROL} [&.p-focus]:border-red-500!` } } : undefined}
        ptOptions={{ mergeSections: true, mergeProps: true }}
        className={`p-inputtext-sm ${MOTION}`}
      />
      {invalid ? <AddFieldMessage id={messageId} message={error} /> : null}
    </label>
  );
};

/** The columns a CSV row carries; `designation` is the identity the import upserts on, not a value. */
const IMPORTED_FIELDS = [
  'nominal_diameter_mm',
  'pitch',
  'pre_drill_diameter_mm',
  'thread_type',
  'alternative_depth_mm',
  'depth_offset_a_mm',
  'method',
] as const satisfies readonly (keyof ThreadSpecModel)[];

/** What one CSV import did to the table, in the three numbers the toast reads out. */
export interface ThreadImportDiff {
  /** Designations the table did not have before this import. */
  added: number;
  /** Rows that were already there and whose imported columns moved. */
  updated: number;
  /** Rows the file carried that the table already held with the same values. */
  unchanged: number;
}

/**
 * Work out what a CSV import changed by comparing the table around it.
 *
 * The backend upserts on `designation` and reports only how many rows it wrote, so the
 * split has to be read off the two snapshots: a designation absent before is new, one
 * present before with a different imported column is updated.
 *
 * `loaded` is what bounds the rest. The fresh table also holds rows the file never mentioned
 * — those were not touched and must not be counted — so `unchanged` is what the import wrote
 * minus what it changed, rather than every row that happens to have stayed the same.
 */
export function diffImportedSpecs(
  before: readonly ThreadSpecModel[],
  after: readonly ThreadSpecModel[],
  loaded: number
): ThreadImportDiff {
  const previous = new Map(before.map((spec) => [spec.designation, spec]));

  let added = 0;
  let updated = 0;
  for (const spec of after) {
    const earlier = previous.get(spec.designation);
    if (!earlier) {
      added += 1;
    } else if (IMPORTED_FIELDS.some((field) => earlier[field] !== spec[field])) {
      updated += 1;
    }
  }

  return { added, updated, unchanged: Math.max(0, loaded - added - updated) };
}

export const ThreadManagement: FC = () => {
  const { t } = useTranslation();
  const { specs, isLoading, error, refetch } = useThreadSpecs();

  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  /** Row queued for deletion; null closes that confirmation. */
  const [deleteTarget, setDeleteTarget] = useState<ThreadSpecModel | null>(null);
  const [isDeletingRow, setIsDeletingRow] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState<AddThreadForm>(EMPTY_ADD_FORM);
  const [isAdding, setIsAdding] = useState(false);
  /** Fields the user has already left; a blank required one stays quiet until then. */
  const [addTouched, setAddTouched] = useState<ReadonlySet<AddField>>(() => new Set());
  /** Set by a click on the submit button, which is what makes every blank field speak up. */
  const [addSubmitAttempted, setAddSubmitAttempted] = useState(false);
  /** The add dialog's inputs, so a rejected submit can put the caret on the first bad one. */
  const addFieldRefs = useRef<Partial<Record<AddNumericField, HTMLInputElement | null>>>({});
  /** The method dropdown, which is focused through PrimeReact's handle rather than a DOM node. */
  const addMethodRef = useRef<Dropdown>(null);
  const [isExporting, setIsExporting] = useState(false);
  /** CSV import lives in a dialog: it is a rare action and does not earn room on the page. */
  const [showImportDialog, setShowImportDialog] = useState(false);
  const rowsPerPageId = useId();
  const rowInteractionHelpRef = useRef<OverlayPanel>(null);

  const [edits, setEdits] = useState<ThreadEditsMap>({});
  /** Latest `edits` for submit — avoids a stale `useCallback` closure. */
  const editsRef = useRef<ThreadEditsMap>({});
  editsRef.current = edits;
  const [submitting, setSubmitting] = useState(false);
  const [editingCell, setEditingCell] = useState<{ id: string; field: NumericField } | null>(null);
  /**
   * The one classification cell whose dropdown is open, kept apart from `editingCell` so the
   * numeric editor and the choice editor cannot both claim the same cell.
   */
  const [editingChoiceCell, setEditingChoiceCell] = useState<{ id: string; field: ChoiceField } | null>(null);
  /** Raw text of the numeric cell being edited, kept as typed until it is committed. */
  const [draft, setDraft] = useState('');

  const [colWidths, setColWidths] = useState<Record<string, number>>(loadColWidths);
  const colWidthsRef = useRef(colWidths);
  colWidthsRef.current = colWidths;
  const resizingRef = useRef<{ col: string; startX: number; startWidth: number } | null>(null);

  /**
   * Drag the right edge of a header to resize that column, persisting on release.
   *
   * The listeners live on `document` rather than the handle so the pointer may leave the thin
   * grab area mid-drag without dropping the gesture.
   */
  const handleResizeMouseDown = useCallback((col: string, e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidthsRef.current[col] ?? DEFAULT_COL_WIDTHS[col] ?? 80;
    resizingRef.current = { col, startX, startWidth };

    const onMouseMove = (ev: MouseEvent) => {
      const active = resizingRef.current;
      if (!active) return;
      const width = Math.max(MIN_COL_WIDTH, active.startWidth + (ev.clientX - active.startX));
      setColWidths((prev) => ({ ...prev, [active.col]: width }));
    };

    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setColWidths((prev) => {
        try {
          localStorage.setItem(LS_COL_WIDTHS_KEY, JSON.stringify(prev));
        } catch {
          // ignore
        }
        return prev;
      });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  /** Stable so the editor is focused once on mount rather than on every keystroke. */
  const focusEditor = useCallback((el: HTMLInputElement | null) => {
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  // Page and size live in the URL, like `useTools`, so a reload keeps the reading position.
  const page = Math.max(1, Math.trunc(Number(searchParams.get('page') ?? '1')) || 1);
  const sizeParam = Math.trunc(Number(searchParams.get('size') ?? DEFAULT_PAGE_SIZE));
  const pageSize = PAGE_SIZE_OPTIONS.includes(sizeParam) ? sizeParam : DEFAULT_PAGE_SIZE;

  const setPage = useCallback(
    (next: number) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('page', String(next));
        return p;
      });
    },
    [setSearchParams]
  );

  const setPageSize = useCallback(
    (next: number) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('size', String(next));
        p.set('page', '1');
        return p;
      });
    },
    [setSearchParams]
  );

  const totalPages = Math.max(1, Math.ceil(specs.length / pageSize));
  // Clamp for this render as well as in the URL: an import or a delete can shrink the set
  // under the current page, and slicing past the end would flash an empty table first.
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);

  const pagedSpecs = useMemo(
    () => specs.slice((safePage - 1) * pageSize, (safePage - 1) * pageSize + pageSize),
    [specs, safePage, pageSize]
  );

  const totalTableWidth =
    THREAD_COLUMNS.reduce((sum, col) => sum + (colWidths[col.key] ?? DEFAULT_COL_WIDTHS[col.key] ?? 0), 0) +
    (colWidths[ACTIONS_COL_KEY] ?? DEFAULT_COL_WIDTHS[ACTIONS_COL_KEY] ?? 0);

  const dirtyCount = Object.keys(edits).length;
  const isFieldDirty = (id: string, field: EditableField): boolean => {
    const patch = edits[id];
    return patch != null && field in patch;
  };

  /** The value to show: the pending edit when there is one, otherwise what the server sent. */
  const shownNumber = (spec: ThreadSpecModel, field: NumericField): number | null => {
    const patch = edits[spec.id];
    if (patch && field in patch) return (patch[field] ?? null) as number | null;
    return spec[field];
  };

  const shownThreadType = (spec: ThreadSpecModel): ThreadType => edits[spec.id]?.thread_type ?? spec.thread_type;

  const shownMethod = (spec: ThreadSpecModel): ThreadMethod | null => {
    const patch = edits[spec.id];
    // `null` is a real edited value here, so presence has to be tested rather than falsiness.
    if (patch && 'method' in patch) return patch.method ?? null;
    return spec.method;
  };

  /**
   * Record one cell change, or drop it again when the value returns to what the server holds,
   * so a round trip back to the original leaves no phantom "unsaved" state behind.
   */
  const setEdit = useCallback((spec: ThreadSpecModel, field: EditableField, value: number | string | null) => {
    setEdits((prev) => {
      const next = { ...prev };
      const row: UpdateThreadSpec = { ...(next[spec.id] ?? {}) };
      if (value === spec[field]) {
        delete row[field];
      } else {
        (row as Record<string, unknown>)[field] = value;
      }
      if (Object.keys(row).length === 0) delete next[spec.id];
      else next[spec.id] = row;
      editsRef.current = next;
      return next;
    });
  }, []);

  const startEdit = (spec: ThreadSpecModel, field: NumericField) => {
    const current = shownNumber(spec, field);
    setDraft(current == null ? '' : formatMm(current));
    setEditingCell({ id: spec.id, field });
  };

  /**
   * Validate and store one numeric cell. Returns whether the text was usable, so Enter can
   * keep a bad value on screen for correction while blur closes the editor either way — a
   * blur that refused to close would trap focus in the cell.
   */
  const commitNumeric = (spec: ThreadSpecModel, field: NumericField, raw: string): boolean => {
    // Czech keyboards produce a decimal comma, and the table's own designations use one.
    const text = raw.trim().replace(',', '.');
    if (text === '') {
      if (field === NULLABLE_NUMERIC) {
        setEdit(spec, field, null);
        return true;
      }
      toast.error(t('threads.editPositiveRequired'));
      return false;
    }
    const parsed = Number(text);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error(t('threads.editPositiveRequired'));
      return false;
    }
    setEdit(spec, field, parsed);
    return true;
  };

  /** Memoised so the callbacks that clear pending edits can depend on it honestly. */
  const handleDiscard = useCallback(() => {
    setEdits({});
    editsRef.current = {};
    setEditingCell(null);
  }, []);

  const handleSave = useCallback(async () => {
    const snapshot = editsRef.current;
    const ids = Object.keys(snapshot);
    if (ids.length === 0) return;

    setSubmitting(true);
    setEditingCell(null);
    // One PUT per edited row, in parallel: the rows are independent, so one bad row should
    // not hold up the rest or leave the earlier ones unsaved.
    const results = await Promise.allSettled(ids.map((id) => threadsApi.update(id, snapshot[id] as UpdateThreadSpec)));

    const failedIds: string[] = [];
    results.forEach((result, index) => {
      if (result.status !== 'rejected') return;
      const id = ids[index] as string;
      failedIds.push(id);
      const designation = specs.find((spec) => spec.id === id)?.designation ?? id;
      console.error(`Thread spec update failed for ${designation}:`, result.reason);
      toast.error(t('threads.saveRowFailed', { designation }));
    });

    setSubmitting(false);
    if (failedIds.length === 0) {
      setEdits({});
      editsRef.current = {};
      toast.success(t('threads.saveSuccess'));
    } else {
      // Keep the rows that failed pending, so their edits are not silently lost.
      const kept: ThreadEditsMap = {};
      for (const id of failedIds) {
        const patch = snapshot[id];
        if (patch) kept[id] = patch;
      }
      setEdits(kept);
      editsRef.current = kept;
    }
    await refetch();
  }, [refetch, specs, t]);

  /**
   * A fresh import renumbers the table, so send the reader back to the top and drop stale edits,
   * then say what the run actually did.
   *
   * The rows on screen are the table as it stood before the upload, and `refetch` hands back
   * the reloaded one, which is everything `diffImportedSpecs` needs — the backend only counts
   * the rows it wrote and cannot tell an insert from an overwrite.
   */
  const handleImported = useCallback(
    async (report: ThreadTableImportReport) => {
      handleDiscard();
      setPage(1);

      // `specs` is read before `refetch` is awaited, so it is the table as the import found it.
      const { added, updated, unchanged } = diffImportedSpecs(specs, await refetch(), report.loaded);
      const segments = [added > 0 ? t('threads.importSummaryNew', { count: added }) : t('threads.importSummaryNoNew')];
      if (updated > 0) segments.push(t('threads.importSummaryUpdated', { count: updated }));
      if (unchanged > 0) segments.push(t('threads.importSummaryUnchanged', { count: unchanged }));

      toast.success(t('threads.importSummary', { parts: `${segments.join(', ')}.` }));
    },
    [handleDiscard, refetch, setPage, specs, t]
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      downloadBlob(await threadsApi.exportCsv(), EXPORT_FILENAME);
    } catch (err) {
      console.error('Thread table export failed:', err);
      toast.error(t('threads.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  const addDiameter = parsePositive(addForm.nominalDiameter);
  const addPitch = parsePositive(addForm.pitch);
  const addPreDrill = parsePositive(addForm.preDrillDiameter);
  const addDepth = parsePositive(addForm.depth);
  const addOffsetText = addForm.depthOffset.trim();
  const addOffset = parsePositive(addForm.depthOffset);
  const derivedDesignation = deriveDesignation(addDiameter, addPitch, addForm.threadType);

  const addStatuses = useMemo(() => {
    const statuses = {} as Record<AddField, AddFieldStatus>;
    for (const field of ADD_NUMERIC_FIELDS) {
      statuses[field] = addFieldStatus(addForm[field], field === ADD_OPTIONAL_FIELD);
    }
    // The method is picked from a list rather than typed, so it can only ever be missing.
    statuses.method = addForm.method == null ? 'missing' : 'ok';
    return statuses;
  }, [addForm]);

  const addValid = ADD_FIELDS.every((field) => addStatuses[field] === 'ok');

  /**
   * The message each field is currently showing, if any.
   *
   * A value that cannot be read as a number is called out while it is being typed, because the
   * user is looking straight at it. A blank required field waits: complaining about fields the
   * user has not reached yet is what makes a freshly opened dialog look broken.
   */
  const addErrors = useMemo(() => {
    const messages = {} as Record<AddField, string | null>;
    for (const field of ADD_FIELDS) {
      const status = addStatuses[field];
      if (status === 'invalid') {
        messages[field] = t('threads.editPositiveRequired');
      } else if (status === 'missing' && (addSubmitAttempted || addTouched.has(field))) {
        // A dropdown is picked, not filled in, so it gets its own wording.
        messages[field] = t(field === 'method' ? 'threads.addSelectRequired' : 'threads.addRequired');
      } else {
        messages[field] = null;
      }
    }
    return messages;
  }, [addStatuses, addSubmitAttempted, addTouched, t]);

  const handleAddFieldChange = useCallback((field: AddNumericField, value: string) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddFieldBlur = useCallback((field: AddField) => {
    setAddTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
  }, []);

  const handleMethodChange = useCallback((method: ThreadMethod | null) => {
    setAddForm((prev) => ({ ...prev, method }));
  }, []);

  const handleMethodTouched = useCallback(() => handleAddFieldBlur('method'), [handleAddFieldBlur]);

  const registerAddField = useCallback((field: AddNumericField, el: HTMLInputElement | null) => {
    addFieldRefs.current[field] = el;
  }, []);

  const closeAddDialog = () => {
    setShowAddDialog(false);
    setAddForm(EMPTY_ADD_FORM);
    setAddTouched(new Set());
    setAddSubmitAttempted(false);
  };

  const handleCreate = async () => {
    if (isAdding) return;
    if (!addValid) {
      // The button stays clickable on an invalid form so that the click can explain itself:
      // every field starts speaking, the summary appears, and the caret lands on the first
      // one to fix. A disabled button says only that something is wrong, never what.
      setAddSubmitAttempted(true);
      setAddTouched(new Set(ADD_FIELDS));
      const firstBad = ADD_FIELDS.find((field) => addStatuses[field] !== 'ok');
      if (firstBad === 'method') {
        addMethodRef.current?.focus();
      } else if (firstBad) {
        addFieldRefs.current[firstBad]?.focus();
      }
      return;
    }
    const { method } = addForm;
    if (addDiameter == null || addPitch == null || addPreDrill == null || addDepth == null || method == null) return;
    const designation = addForm.designation.trim();
    const body: CreateThreadSpec = {
      // biome-ignore lint/style/useNamingConvention: API uses snake_case
      nominal_diameter_mm: addDiameter,
      pitch: addPitch,
      // biome-ignore lint/style/useNamingConvention: API uses snake_case
      pre_drill_diameter_mm: addPreDrill,
      // biome-ignore lint/style/useNamingConvention: API uses snake_case
      thread_type: addForm.threadType,
      // biome-ignore lint/style/useNamingConvention: API uses snake_case
      alternative_depth_mm: addDepth,
      // Omitted rather than null: the backend derives the designation only when the key is absent.
      ...(designation ? { designation } : {}),
      // biome-ignore lint/style/useNamingConvention: API uses snake_case
      ...(addOffsetText === '' ? {} : { depth_offset_a_mm: addOffset }),
      method,
    };

    setIsAdding(true);
    try {
      const created = await threadsApi.create(body);
      toast.success(t('threads.addSuccess', { designation: created.designation }));
      closeAddDialog();
      setPage(1);
      await refetch();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      console.error('Thread creation failed:', err);
      if (status === 409) {
        toast.error(t('threads.addDuplicate', { designation: designation || derivedDesignation }));
      } else {
        toast.error(t('threads.addFailed'));
      }
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteRow = async () => {
    if (!deleteTarget) return;
    const { id, designation } = deleteTarget;
    setIsDeletingRow(true);
    try {
      await threadsApi.remove(id);
      toast.success(t('threads.deleteRowSuccess', { designation }));
      setDeleteTarget(null);
      // The row is gone, so any pending edit against it would post to a dead id.
      setEdits((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        editsRef.current = next;
        return next;
      });
      setEditingCell((prev) => (prev?.id === id ? null : prev));
      await refetch();
    } catch (err) {
      console.error('Thread deletion failed:', err);
      toast.error(t('threads.deleteRowFailed'));
    } finally {
      setIsDeletingRow(false);
    }
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const report = await threadsApi.deleteAll();
      toast.success(t('threads.deleteAllSuccess', { deleted: report.deleted }));
      setShowDeleteDialog(false);
      handleDiscard();
      setPage(1);
      await refetch();
    } catch (err) {
      console.error('Thread table delete-all failed:', err);
      toast.error(t('threads.deleteAllFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const threadTypeOptions = useMemo(
    () => [
      { label: t('threads.typeStandard'), value: 'standard' },
      { label: t('threads.typeFine'), value: 'fine' },
    ],
    [t]
  );

  const methodOptions = useMemo(
    () => [
      { label: t('threads.methodTap'), value: 'tap' },
      { label: t('threads.methodThreadMill'), value: 'thread_mill' },
    ],
    [t]
  );

  /**
   * A thin bar on the cell's leading edge marks a pending edit.
   *
   * It beats the amber wash the table used before: it says "this one changed" without
   * repainting a whole column of values in a second colour.
   */
  const pendingAccent = (id: string, field: EditableField) =>
    isFieldDirty(id, field) ? (
      <span className="absolute inset-y-0 left-0 w-[3px] bg-amber-400" aria-hidden="true" />
    ) : null;

  const numericCell = (spec: ThreadSpecModel, field: NumericField) => {
    const isEditing = editingCell?.id === spec.id && editingCell.field === field;
    const value = shownNumber(spec, field);

    if (isEditing) {
      return (
        <InputText
          ref={focusEditor}
          value={draft}
          inputMode="decimal"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            commitNumeric(spec, field, draft);
            setEditingCell(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setEditingCell(null);
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              if (commitNumeric(spec, field, draft)) setEditingCell(null);
            }
          }}
          className="w-full p-inputtext-sm text-right"
          aria-label={t('threads.editValue')}
        />
      );
    }

    return (
      <button
        type="button"
        onClick={() => startEdit(spec, field)}
        title={t('threads.editValue')}
        className={`${CELL_VALUE_CLASSES} text-right tabular-nums`}
      >
        {value == null ? EMPTY_VALUE : formatMm(value)}
      </button>
    );
  };

  /**
   * The type and production-method columns: plain text until double-clicked, then a dropdown
   * that opens on its own and closes once it is done.
   *
   * The value still travels through `setEdit`, so a pick lands in the same pending-edit map the
   * always-visible dropdown wrote to and is saved by the same batch `PUT`.
   */
  const choiceCell = (spec: ThreadSpecModel, field: ChoiceField) => {
    const isEditing = editingChoiceCell?.id === spec.id && editingChoiceCell.field === field;
    const isType = field === 'thread_type';
    const value = isType ? shownThreadType(spec) : shownMethod(spec);
    const options = isType ? threadTypeOptions : methodOptions;
    const label = t(isType ? 'threads.colType' : 'threads.colMethod');

    if (isEditing) {
      return (
        <ChoiceCellEditor
          value={value}
          options={options}
          ariaLabel={label}
          placeholder={EMPTY_VALUE}
          onChange={(picked) => {
            setEditingChoiceCell(null);
            setEdit(spec, field, picked as ThreadType | ThreadMethod | null);
          }}
          onClose={() => setEditingChoiceCell(null)}
        />
      );
    }

    const shown = options.find((option) => option.value === value)?.label ?? EMPTY_VALUE;
    return (
      // Opened by a single click, like every numeric cell on this page: two different gestures
      // for "edit this cell" in one table would be the harder thing to learn.
      <button
        type="button"
        onClick={() => setEditingChoiceCell({ id: spec.id, field })}
        title={t('threads.editValue')}
        className={`${CELL_VALUE_CLASSES} text-left`}
      >
        {shown}
      </button>
    );
  };

  const importButton = (
    <ActionButton icon={<UploadSimple size={16} aria-hidden="true" />} onClick={() => setShowImportDialog(true)}>
      {t('threads.importButton')}
    </ActionButton>
  );

  const addThreadButton = (
    <ActionButton variant="primary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setShowAddDialog(true)}>
      {t('threads.addThread')}
    </ActionButton>
  );

  return (
    <div className="flex flex-col gap-6">
      <Reveal>
        <PageHeader
          title={t('threads.title')}
          subtitle={isLoading ? undefined : t('threads.countTotal', { total: specs.length })}
          backTo={{ to: '/', label: t('threads.backToProjects') }}
          actions={
            dirtyCount > 0 ? (
              <>
                <ActionButton variant="ghost" onClick={handleDiscard} disabled={submitting}>
                  {t('threads.discardChanges')}
                </ActionButton>
                <ActionButton variant="primary" loading={submitting} onClick={handleSave}>
                  {t('threads.saveChanges')}
                  <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold tabular-nums">
                    {dirtyCount}
                  </span>
                </ActionButton>
              </>
            ) : null
          }
        />
      </Reveal>

      {error && <ErrorMessage message={error} />}

      <Reveal delayMs={80}>
        <section aria-labelledby="threads-heading" className="flex flex-col gap-4">
          <h2 id="threads-heading" className="sr-only">
            {t('threads.title')}
          </h2>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ActionButton
                variant="ghost"
                iconOnly={true}
                icon={<Question size={16} aria-hidden="true" />}
                onClick={(e) => rowInteractionHelpRef.current?.toggle(e)}
                aria-label={t('threads.rowInteractionHelpAriaLabel')}
                aria-haspopup="dialog"
              />
              <OverlayPanel
                ref={rowInteractionHelpRef}
                pt={{
                  root: {
                    role: 'dialog',
                    'aria-label': t('threads.rowInteractionHelpAriaLabel'),
                  },
                  content: { className: '!px-4 !py-3' },
                }}
              >
                <ul className="m-0 flex list-none flex-col gap-2 p-0 text-xs text-gray-500">
                  <li>{t('threads.rowInteractionHintEdit')}</li>
                  <li>{t('threads.rowInteractionHintChoice')}</li>
                  <li>{t('threads.rowInteractionHintColumns')}</li>
                </ul>
              </OverlayPanel>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {importButton}
              <ActionButton
                icon={<DownloadSimple size={16} aria-hidden="true" />}
                onClick={handleExport}
                disabled={specs.length === 0 || isExporting}
                loading={isExporting}
              >
                {t('threads.exportCsv')}
              </ActionButton>
              <ActionButton
                variant="danger-ghost"
                icon={<Trash size={16} aria-hidden="true" />}
                onClick={() => setShowDeleteDialog(true)}
                disabled={specs.length === 0}
                aria-label={t('threads.deleteAllTitle')}
              >
                {t('threads.deleteAll')}
              </ActionButton>
              {addThreadButton}
            </div>
          </div>

          {isLoading ? (
            <TableSkeleton rows={10} columns={6} />
          ) : specs.length === 0 ? (
            <div className={TABLE_CARD}>
              <EmptyState
                icon={<ListNumbers size={28} aria-hidden="true" />}
                title={t('threads.emptyTitle')}
                message={t('threads.emptyTable')}
                action={
                  <>
                    {importButton}
                    {addThreadButton}
                  </>
                }
              />
            </div>
          ) : (
            <div className={`${TABLE_CARD} ${CARD_CONTAINMENT}`}>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-sm" style={{ minWidth: totalTableWidth }}>
                  <colgroup>
                    {THREAD_COLUMNS.map((col) => (
                      <col key={col.key} style={{ width: colWidths[col.key] ?? DEFAULT_COL_WIDTHS[col.key] }} />
                    ))}
                    <col style={{ width: colWidths[ACTIONS_COL_KEY] ?? DEFAULT_COL_WIDTHS[ACTIONS_COL_KEY] }} />
                  </colgroup>
                  <thead>
                    <tr className={TABLE_HEADER_ROW}>
                      {THREAD_COLUMNS.map((col) => {
                        const isPinned = col.key === PINNED_COLUMN;
                        // `sticky` is itself a positioned value, so a pinned cell must not also
                        // ask for `relative`: both are position utilities and which one wins
                        // would come down to stylesheet order, not to this class list.
                        const pinnedClasses = isPinned
                          ? `${PINNED_LEFT} ${PINNED_HEADER_Z}`
                          : 'relative';
                        return (
                          <th
                            key={col.key}
                            scope="col"
                            className={`group select-none overflow-hidden px-3 ${TABLE_HEADER_CELL} ${
                              col.align === 'right' ? 'text-right' : 'text-left'
                            } ${pinnedClasses}`}
                          >
                            <span className="block truncate">{t(col.labelKey)}</span>
                            {/* Mouse-only affordance: there is no keyboard gesture to expose,
                                and the widths it writes are a display preference only. */}
                            <div
                              className="absolute right-0 top-0 z-10 flex h-full w-2 cursor-col-resize items-center justify-center"
                              onMouseDown={(e) => handleResizeMouseDown(col.key, e)}
                              aria-hidden="true"
                            >
                              <span
                                className={`block h-3.5 w-px rounded-full bg-gray-200 group-hover:bg-gray-300 ${MOTION}`}
                              />
                            </div>
                          </th>
                        );
                      })}
                      <th scope="col" className={`px-2 ${PINNED_RIGHT} ${PINNED_HEADER_Z}`}>
                        <span className="sr-only">{t('common.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedSpecs.map((spec) => (
                      <tr key={spec.id} className={TABLE_ROW}>
                        <td className={`${TABLE_CELL} overflow-hidden ${PINNED_LEFT} ${PINNED_BODY_Z}`}>
                          <span className="block truncate font-medium text-gray-900">{spec.designation}</span>
                        </td>
                        <td className={TABLE_CELL_NUM}>{formatMm(spec.nominal_diameter_mm)}</td>
                        <td className={`relative ${TABLE_CELL}`}>
                          {pendingAccent(spec.id, 'pitch')}
                          {numericCell(spec, 'pitch')}
                        </td>
                        <td className={`relative ${TABLE_CELL}`}>
                          {pendingAccent(spec.id, 'pre_drill_diameter_mm')}
                          {numericCell(spec, 'pre_drill_diameter_mm')}
                        </td>
                        <td className={`relative ${TABLE_CELL}`}>
                          {pendingAccent(spec.id, 'thread_type')}
                          {choiceCell(spec, 'thread_type')}
                        </td>
                        <td className={`relative ${TABLE_CELL}`}>
                          {pendingAccent(spec.id, 'alternative_depth_mm')}
                          {numericCell(spec, 'alternative_depth_mm')}
                        </td>
                        <td className={`relative ${TABLE_CELL}`}>
                          {pendingAccent(spec.id, 'depth_offset_a_mm')}
                          {numericCell(spec, 'depth_offset_a_mm')}
                        </td>
                        <td className={`relative ${TABLE_CELL}`}>
                          {pendingAccent(spec.id, 'method')}
                          {choiceCell(spec, 'method')}
                        </td>
                        <td className={`px-2 text-right align-middle ${PINNED_RIGHT} ${PINNED_BODY_Z}`}>
                          <ActionButton
                            variant="danger-ghost"
                            iconOnly={true}
                            icon={<Trash size={15} aria-hidden="true" />}
                            onClick={() => setDeleteTarget(spec)}
                            aria-label={t('threads.deleteRowAria', { designation: spec.designation })}
                            title={t('threads.deleteRowAria', { designation: spec.designation })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={`${TABLE_FOOTER} flex-wrap gap-2`}>
                <div className="flex shrink-0 items-center gap-2">
                  <label htmlFor={rowsPerPageId} className="whitespace-nowrap text-sm text-gray-600">
                    {t('threads.rowsPerPage')}
                  </label>
                  <Dropdown
                    inputId={rowsPerPageId}
                    value={pageSize}
                    options={PAGE_SIZE_OPTIONS}
                    onChange={(e) => setPageSize(Number(e.value))}
                    pt={PAGE_SIZE_DROPDOWN_PT}
                    dropdownIcon={<CaretDown size={14} aria-hidden="true" />}
                    aria-label={t('threads.rowsPerPage')}
                  />
                </div>

                <div className="flex flex-1 items-center justify-center gap-2">
                  <ActionButton
                    variant="ghost"
                    iconOnly={true}
                    icon={<CaretLeft size={16} aria-hidden="true" />}
                    disabled={safePage <= 1}
                    onClick={() => setPage(Math.max(1, safePage - 1))}
                    aria-label={t('common.previousPage')}
                  />
                  <span className="text-sm text-gray-600">
                    {`${t('common.page')} ${safePage} ${t('common.of')} ${totalPages}`}
                  </span>
                  <ActionButton
                    variant="ghost"
                    iconOnly={true}
                    icon={<CaretRight size={16} aria-hidden="true" />}
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(safePage + 1)}
                    aria-label={t('common.nextPage')}
                  />
                </div>

                <span className="shrink-0 text-sm text-gray-600">
                  {`${Math.min(safePage * pageSize, specs.length)} / ${specs.length}`}
                </span>
              </div>
            </div>
          )}
        </section>
      </Reveal>

      <Dialog
        header={t('threads.importDialogTitle')}
        visible={showImportDialog}
        onHide={() => setShowImportDialog(false)}
        modal={true}
        className="w-full max-w-lg"
        pt={TOOL_MANAGEMENT_DIALOG_PT}
      >
        <ThreadTableDropZone
          onImported={async (report) => {
            setShowImportDialog(false);
            await handleImported(report);
          }}
        />
      </Dialog>

      <ConfirmDeleteDialog
        visible={showDeleteDialog}
        title={t('threads.deleteAllTitle')}
        message={t('threads.deleteAllConfirm', { count: specs.length })}
        isDeleting={isDeleting}
        onConfirm={handleDeleteAll}
        onCancel={() => setShowDeleteDialog(false)}
      />

      <ConfirmDeleteDialog
        visible={deleteTarget !== null}
        title={t('threads.deleteRowTitle')}
        message={t('threads.deleteRowConfirm', { designation: deleteTarget?.designation ?? '' })}
        isDeleting={isDeletingRow}
        onConfirm={handleDeleteRow}
        onCancel={() => setDeleteTarget(null)}
      />

      <Dialog
        header={t('threads.addThread')}
        visible={showAddDialog}
        onHide={() => {
          // A dismissal mid-submit would strand the request, so it is ignored until it lands.
          if (!isAdding) closeAddDialog();
        }}
        closable={!isAdding}
        modal={true}
        className="w-full max-w-lg"
        pt={TOOL_MANAGEMENT_DIALOG_PT}
        footer={
          <div className="flex justify-end gap-2">
            <ActionButton variant="ghost" onClick={closeAddDialog} disabled={isAdding}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton variant="primary" onClick={handleCreate} disabled={isAdding} loading={isAdding}>
              {t('threads.addSubmit')}
            </ActionButton>
          </div>
        }
      >
        <div className="flex flex-col gap-6 pt-1">
          <div className="flex flex-col gap-2">
            <span className={SECTION_LABEL}>{t('threads.addSectionDimensions')}</span>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <AddNumberField
                field="nominalDiameter"
                label={t('threads.colNominalDiameter')}
                value={addForm.nominalDiameter}
                error={addErrors.nominalDiameter}
                onChange={handleAddFieldChange}
                onBlur={handleAddFieldBlur}
                registerRef={registerAddField}
              />
              <AddNumberField
                field="pitch"
                label={t('threads.colPitch')}
                value={addForm.pitch}
                error={addErrors.pitch}
                onChange={handleAddFieldChange}
                onBlur={handleAddFieldBlur}
                registerRef={registerAddField}
              />
              <label className="flex flex-col gap-1 text-sm text-gray-700">
                {t('threads.colType')}
                <Dropdown
                  value={addForm.threadType}
                  options={threadTypeOptions}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, threadType: e.value as ThreadType }))}
                  className="p-inputtext-sm"
                />
              </label>
              <AddNumberField
                field="preDrillDiameter"
                label={t('threads.colPreDrillDiameter')}
                value={addForm.preDrillDiameter}
                error={addErrors.preDrillDiameter}
                onChange={handleAddFieldChange}
                onBlur={handleAddFieldBlur}
                registerRef={registerAddField}
              />
              <AddNumberField
                field="depth"
                label={t('threads.colDepth')}
                value={addForm.depth}
                error={addErrors.depth}
                onChange={handleAddFieldChange}
                onBlur={handleAddFieldBlur}
                registerRef={registerAddField}
              />
              <AddNumberField
                field="depthOffset"
                label={t('threads.colDepthOffsetA')}
                value={addForm.depthOffset}
                error={addErrors.depthOffset}
                onChange={handleAddFieldChange}
                onBlur={handleAddFieldBlur}
                registerRef={registerAddField}
              />
              <AddMethodField
                label={t('threads.colMethod')}
                value={addForm.method}
                options={methodOptions}
                placeholder={t('threads.selectMethod')}
                error={addErrors.method}
                onChange={handleMethodChange}
                onTouched={handleMethodTouched}
                dropdownRef={addMethodRef}
              />
            </div>
          </div>

          <div className="border-t border-gray-200" />

          <div className="flex flex-col gap-2">
            <span className={SECTION_LABEL}>{t('threads.colDesignation')}</span>
            <div className="flex flex-col gap-1 rounded-lg bg-gray-50 px-3 py-2">
              <span className="text-xs text-gray-500">{t('threads.designationPreview')}</span>
              <span className="font-medium tabular-nums text-gray-900">
                {addForm.designation.trim() || derivedDesignation || EMPTY_VALUE}
              </span>
            </div>
            <label className="flex flex-col gap-1 text-sm text-gray-700">
              {t('threads.designationOverride')}
              <InputText
                value={addForm.designation}
                onChange={(e) => setAddForm((prev) => ({ ...prev, designation: e.target.value }))}
                placeholder={derivedDesignation}
                className="p-inputtext-sm"
              />
            </label>
          </div>

          {addSubmitAttempted && !addValid ? (
            <div role="alert" className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              <WarningCircle size={16} className="shrink-0" aria-hidden="true" />
              {t('threads.addFixFields')}
            </div>
          ) : null}
        </div>
      </Dialog>
    </div>
  );
};
